// Spec 45 component-behavior — cmp_ds_tabs + aria_tabs.
//
// Three composing pieces per the WAI-ARIA tab pattern:
//   * <Tabs> — controlled root holding the active value.
//   * <TabList> — role='tablist'; arrow-key navigation per
//     ARIA spec (Left/Right OR Up/Down).
//   * <Tab> — role='tab'; aria-selected reflects active state;
//     aria-controls binds the panel id.
//   * <TabPanel> — role='tabpanel'; only mounts when active OR
//     keepMounted=true (data-fetch optimization for activation
//     ='manual' surfaces).
//
// Activation modes per spec:
//   * automatic (default) — focus IS selection. Arrow keys move
//     focus AND change the value. Use for lightweight views where
//     swap is cheap.
//   * manual — arrow keys move focus only; Enter/Space activates.
//     Use when activation triggers data fetching (avoids spamming
//     the network on arrow-spam).
//
// Variants underline/pill/segmented are CSS-side; the component
// just emits data-variant for tenant skinning.

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useId,
  useMemo,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export type TabsVariant = "underline" | "pill" | "segmented";
export type TabsActivation = "automatic" | "manual";

interface TabsContextValue {
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly activation: TabsActivation;
  readonly variant: TabsVariant;
  readonly tabsId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx === null) {
    throw new Error("<Tab>/<TabList>/<TabPanel> must be used inside <Tabs>");
  }
  return ctx;
}

export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly variant?: TabsVariant;
  readonly activation?: TabsActivation;
  readonly children?: ReactNode;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      value,
      onValueChange,
      variant = "underline",
      activation = "automatic",
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const ctx = useMemo<TabsContextValue>(
      () => ({ value, onValueChange, activation, variant, tabsId: generatedId }),
      [value, onValueChange, activation, variant, generatedId],
    );
    return (
      <TabsContext.Provider value={ctx}>
        <div
          ref={ref}
          data-component="tabs"
          data-variant={variant}
          data-activation={activation}
          className={cx("nai-tabs", className)}
          {...rest}
        >
          {children}
        </div>
      </TabsContext.Provider>
    );
  },
);
Tabs.displayName = "Tabs";

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  readonly "aria-label": string;
  readonly children?: ReactNode;
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(
  ({ "aria-label": ariaLabel, onKeyDown, className, children, ...rest }, ref) => {
    const { activation, onValueChange } = useTabsContext();

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const tabs = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>(
            'button[role="tab"]:not(:disabled)',
          ),
        );
        if (tabs.length === 0) {
          onKeyDown?.(event);
          return;
        }
        const target = event.target as HTMLButtonElement | null;
        if (target === null || !tabs.includes(target)) {
          onKeyDown?.(event);
          return;
        }
        const idx = tabs.indexOf(target);
        let nextIdx = -1;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextIdx = (idx + 1) % tabs.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextIdx = (idx - 1 + tabs.length) % tabs.length;
        } else if (event.key === "Home") {
          nextIdx = 0;
        } else if (event.key === "End") {
          nextIdx = tabs.length - 1;
        }
        if (nextIdx >= 0) {
          event.preventDefault();
          const next = tabs[nextIdx];
          if (next !== undefined) {
            next.focus();
            // automatic activation: focus IS selection.
            if (activation === "automatic") {
              const v = next.dataset["tabValue"];
              if (v !== undefined) onValueChange(v);
            }
          }
          return;
        }
        onKeyDown?.(event);
      },
      [activation, onValueChange, onKeyDown],
    );

    // tablist itself is NOT focusable (children Tabs are; roving
    // tabindex pattern). jsx-a11y wants role-bearing elements
    // focusable; the WAI-ARIA tab pattern explicitly uses non-
    // focusable tablist with focusable tab children — disable the
    // rule with a justification comment.
    return (
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus
      <div
        ref={ref}
        role="tablist"
        aria-label={ariaLabel}
        data-component="tab-list"
        className={cx("nai-tab-list", className)}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
TabList.displayName = "TabList";

export interface TabProps extends Omit<HTMLAttributes<HTMLButtonElement>, "type"> {
  readonly value: string;
  readonly disabled?: boolean;
  readonly children?: ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ value, disabled, className, onClick, children, ...rest }, ref) => {
    // `activation` lives on the context but is read by <TabList> for
    // arrow-key behavior, NOT here — click always activates regardless
    // of mode (manual: click is the explicit activation; automatic: click
    // is the user's selection signal too).
    const { value: activeValue, onValueChange, tabsId } = useTabsContext();
    const isActive = activeValue === value;
    const tabId = `${tabsId}-tab-${value}`;
    const panelId = `${tabsId}-panel-${value}`;

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        id={tabId}
        aria-selected={isActive}
        aria-controls={panelId}
        // Roving tabindex: only the active tab is in the tab order.
        tabIndex={isActive ? 0 : -1}
        disabled={disabled === true}
        data-component="tab"
        data-tab-value={value}
        data-active={isActive || undefined}
        className={cx("nai-tab", className)}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented && !disabled) {
            onValueChange(value);
          }
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
Tab.displayName = "Tab";

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  readonly value: string;
  /** Keep the panel mounted (and just hide it) when not active — useful for preserving form state across tab switches. */
  readonly keepMounted?: boolean;
  readonly children?: ReactNode;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  ({ value, keepMounted = false, className, children, ...rest }, ref) => {
    const { value: activeValue, tabsId } = useTabsContext();
    const isActive = activeValue === value;
    const panelId = `${tabsId}-panel-${value}`;
    const tabId = `${tabsId}-tab-${value}`;

    if (!isActive && !keepMounted) return null;

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId}
        tabIndex={0}
        hidden={!isActive}
        data-component="tab-panel"
        data-active={isActive || undefined}
        className={cx("nai-tab-panel", className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
TabPanel.displayName = "TabPanel";
